<script setup>
/* global axios */
// Aba Configurações do módulo de Atendimento.
// Por ora contém apenas o WEBHOOK (dispara POST em criado/atualizado/cancelado).
// Os demais campos da AppointmentConfig (nome/fuso/regras de agendamento) existem
// na tabela mas ainda NÃO têm comportamento no sistema — por isso não são expostos
// aqui (não mostrar controle que não faz nada).
import { ref, computed, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useModulePermissions } from 'dashboard/composables/useModulePermissions';
import GoogleCalendarEventOwnerSetting from './GoogleCalendarEventOwnerSetting.vue';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;
// Gating de ações por FUNÇÃO: esconde criar/editar/excluir conforme a permissão
// CRUD do módulo appointments (o backend recusa de todo jeito; o botão some).
const { moduleCan } = useModulePermissions();
const canCreateAppointments = computed(() =>
  moduleCan('create', 'appointments')
);
const canEditAppointments = computed(() => moduleCan('update', 'appointments'));
const canDeleteAppointments = computed(() =>
  moduleCan('delete', 'appointments')
);

const loading = ref(true);
// Motivos de cancelamento e falta (issue #47): com eles cadastrados, a agenda
// passa a EXIGIR motivo ao cancelar/marcar falta, e o relatório ganha sentido.
const reasons = ref([]);
const novoMotivo = reactive({ kind: 'cancelled', name: '' });
const loadReasons = async () => {
  reasons.value =
    (await axios.get(`${base}/appointment_reasons`)).data.data || [];
};
const addReason = async () => {
  if (!novoMotivo.name.trim()) return;
  await axios.post(`${base}/appointment_reasons`, {
    kind: novoMotivo.kind,
    name: novoMotivo.name.trim(),
  });
  novoMotivo.name = '';
  await loadReasons();
};
const removeReason = async r => {
  if (r.in_use) return;
  try {
    await axios.delete(`${base}/appointment_reasons/${r.id}`);
  } catch (e) {
    // o servidor recusa motivo já usado mesmo que a tela deixe passar
    useAlert(
      e?.response?.data?.error || t('ATENDIMENTOS.settings.reasons.removeError')
    );
  }
  await loadReasons();
};
// Motivo usado num atendimento não pode ser apagado (viraria dado órfão no
// histórico e no relatório) — desativar tira ele da hora de escolher sem mexer
// no passado.
const toggleReason = async r => {
  await axios.patch(`${base}/appointment_reasons/${r.id}`, {
    is_active: !r.is_active,
  });
  r.is_active = !r.is_active;
};
// Busca e rolagem porque a lista cresce sem teto: cada motivo novo empurrava a
// página inteira para baixo (issue relatada pelo uso real).
const buscaMotivo = ref('');
const reasonsOf = kind => {
  const q = buscaMotivo.value.trim().toLowerCase();
  return reasons.value.filter(
    r => r.kind === kind && (!q || r.name.toLowerCase().includes(q))
  );
};
const totalDe = kind => reasons.value.filter(r => r.kind === kind).length;

onMounted(async () => {
  await loadReasons();
  loading.value = false;
});
</script>

<template>
  <div class="max-w-2xl p-6 mx-auto">
    <div v-if="loading" class="text-sm text-n-slate-10">
      {{ t('ATENDIMENTOS.settings.loading') }}
    </div>

    <div v-else class="flex flex-col gap-6">
      <GoogleCalendarEventOwnerSetting :account-id="accountId" />
      <!-- Motivos de cancelamento e falta -->
      <section class="p-5 border rounded-xl border-n-weak bg-n-solid-1">
        <h3 class="mb-1 text-base font-semibold text-n-slate-12">
          {{ t('ATENDIMENTOS.settings.reasons.title') }}
        </h3>
        <p class="mb-4 text-sm text-n-slate-10">
          {{ t('ATENDIMENTOS.settings.reasons.desc') }}
        </p>
        <!-- Dois detalhes do CSS base do projeto batem aqui: o <select> vem com
             width:100% (engolia a linha e sobrava um filete para digitar) e com
             margin-bottom:16px (a linha do flex virava 56px, o input e o botão
             esticavam e o select ficava mais baixo que os dois). Daí a largura
             fixa, o shrink-0 e o !mb-0. Em telas estreitas os três empilham. -->
        <div class="flex flex-col items-stretch mb-3 gap-2 sm:flex-row">
          <select
            v-model="novoMotivo.kind"
            class="w-full sm:w-44 shrink-0 !mb-0 pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
          >
            <option value="cancelled">
              {{ t('ATENDIMENTOS.settings.reasons.kindCancelled') }}
            </option>
            <option value="missed">
              {{ t('ATENDIMENTOS.settings.reasons.kindMissed') }}
            </option>
          </select>
          <input
            v-model="novoMotivo.name"
            :placeholder="t('ATENDIMENTOS.settings.reasons.placeholder')"
            class="flex-1 min-w-0 w-full !mb-0 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            @keydown.enter="addReason"
          />
          <button
            v-if="canCreateAppointments"
            class="shrink-0 px-4 py-2 text-sm rounded-lg bg-n-brand text-white disabled:opacity-50"
            :disabled="!novoMotivo.name.trim()"
            @click="addReason"
          >
            {{ t('ATENDIMENTOS.settings.reasons.add') }}
          </button>
        </div>
        <input
          v-if="reasons.length > 5"
          v-model="buscaMotivo"
          :placeholder="t('ATENDIMENTOS.settings.reasons.search')"
          class="w-full !mb-0 mt-1 mb-3 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
        />
        <!-- duas colunas só a partir do tablet: em 390px o nome do motivo ficava
             truncado em "Cliente..." e a coluna virava um filete -->
        <div class="grid grid-cols-1 gap-4 mt-3 sm:grid-cols-2">
          <div v-for="kind in ['cancelled', 'missed']" :key="kind">
            <div class="flex items-baseline gap-1.5 mb-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{
                  kind === 'cancelled'
                    ? t('ATENDIMENTOS.settings.reasons.kindCancelled')
                    : t('ATENDIMENTOS.settings.reasons.kindMissed')
                }}
              </span>
              <span v-if="totalDe(kind)" class="text-xs text-n-slate-10">
                {{ totalDe(kind) }}
              </span>
            </div>
            <div v-if="!totalDe(kind)" class="text-xs text-n-slate-10">
              {{ t('ATENDIMENTOS.settings.reasons.empty') }}
            </div>
            <div
              v-else-if="!reasonsOf(kind).length"
              class="text-xs text-n-slate-10"
            >
              {{ t('ATENDIMENTOS.settings.reasons.notFound') }}
            </div>
            <!-- teto de altura: sem isso a página crescia sem parar conforme a
                 operação cadastrava motivo -->
            <div v-else class="overflow-y-auto max-h-56">
              <div
                v-for="r in reasonsOf(kind)"
                :key="r.id"
                class="flex items-center gap-2 py-1 text-sm border-b text-n-slate-11 border-n-weak last-of-type:border-b-0"
              >
                <span
                  class="flex-1 min-w-0 truncate"
                  :class="{ 'text-n-slate-10 line-through': !r.is_active }"
                >
                  {{ r.name }}
                </span>
                <button
                  v-if="canEditAppointments"
                  v-tooltip.left="
                    r.is_active
                      ? t('ATENDIMENTOS.settings.reasons.deactivate')
                      : t('ATENDIMENTOS.settings.reasons.activate')
                  "
                  class="relative w-9 h-5 p-0 transition-colors rounded-full shrink-0"
                  :class="r.is_active ? 'bg-n-brand' : 'bg-n-slate-5'"
                  @click="toggleReason(r)"
                >
                  <span
                    class="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-[left]"
                    :class="r.is_active ? 'left-[18px]' : 'left-0.5'"
                  />
                </button>
                <button
                  v-if="canDeleteAppointments"
                  v-tooltip.left="
                    r.in_use
                      ? t('ATENDIMENTOS.settings.reasons.inUse')
                      : t('ATENDIMENTOS.settings.reasons.remove')
                  "
                  class="shrink-0 p-1 rounded"
                  :class="
                    r.in_use
                      ? 'text-n-slate-8 cursor-not-allowed'
                      : 'text-n-ruby-11 hover:bg-n-ruby-3'
                  "
                  :disabled="r.in_use"
                  @click="removeReason(r)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
